import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  Box,
  makeStyles,
  Typography,
  IconButton,
  Checkbox,
  Divider
} from "@material-ui/core";

import SaveAltIcon from "@material-ui/icons/SaveAlt";
import ReceiptIcon from "@material-ui/icons/Receipt";

import SmallSearchBox from "../../common/SmallSearchBox";
import { makeTextPlural } from "../../../utils/helpers";
import { connect } from "react-redux";
import filterIcon from "../../../assets/images/icons/filter.svg";
import ReactToPrint from "react-to-print";
import PrintLabels from "../../project/PrintLabels";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import MoveToVehicle from "../blocks/MoveToVehicle";
import { setLoadingStop } from "../../../store/actions/loading";

const useStyles = makeStyles(theme => ({
  toolbarWrapper: {
    height: 40,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2, 2, 3),

    "& > div": {
      display: "flex",
      alignItems: "center"
    }
  },
  button: {
    marginLeft: theme.spacing(1.5)
  },
  iconButton: {
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },
  searchBox: {
    height: 40,
    transition: "width 0.3s",
    marginLeft: 0,
    width: "100%"
  },
  importButton: {
    marginLeft: theme.spacing(1.5),
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(1, 2, 1, 1.5),
    border: "1px solid #DBDCE1",
    "& svg": {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1)
    }
  },
  toolbarActions: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(-1),
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main
    }
  },
  selectedText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: "1rem"
  },
  driverLink: {
    "& svg": {
      transform: "rotateY(180deg)"
    }
  }
}));

function UnscheduledToolbar(props) {
  const {
    billingDetails,
    organisation,
    isPublished,
    searchInputValue,
    setSearchInputValue,
    stops,
    selectedStopsList,
    handleSelectAllClick,
    handleMultipleDownload,
    stopsForPrint,
    deleteStop,
    editStop,
    routeGroups,
    showingRouteId,
    onSearchFocus,
    onSearchBlur
  } = props;

  // General
  const classes = useStyles();

  // States
  const [isPrintingLabel, setPrintingLabel] = useState(false);

  // Variables
  const numSelected = selectedStopsList?.list.length;
  const allStopsSelected = selectedStopsList?.list.length === stops.length;

  // Refs
  const labelsPrintRef = useRef();
  let readyToPrintLabel = useRef(false);

  // Callbacks
  const handleSearchChange = searchInputValue => {
    setSearchInputValue(searchInputValue);
  };

  // Effects
  useEffect(() => {
    if (isPrintingLabel && !readyToPrintLabel.current) {
      readyToPrintLabel.current = true;
    } else if (readyToPrintLabel.current) {
      readyToPrintLabel.current = false;
    }
  }, [isPrintingLabel]);

  return (
    <>
      <div className={classes.toolbarWrapper}>
        <Box overflow="hidden" width="100%">
          <SmallSearchBox
            disableForwardSlashFocus
            placeholder="Search unscheduled stops"
            showSearchField={true}
            className={classes.searchBox}
            searchText={searchInputValue}
            onSearchChanged={handleSearchChange}
            ariaLabel="Search unscheduled stops"
            dataCy="unscheduled-stops-search"
            setFocusedOuter={focused =>
              focused ? onSearchFocus() : onSearchBlur()
            }
          />
        </Box>
        {/*
        <IconButton
          className={clsx(classes.iconButton, classes.button)}
          variant="outlined"
        >
          <img src={filterIcon} alt="" />
        </IconButton>*/}
      </div>
      {!!numSelected && (
        <div className={classes.toolbarActions}>
          <div className={classes.checkboxWrapper}>
            <OptimizedTooltip
              title={
                allStopsSelected ? (
                  "Deselect all"
                ) : (
                  <>
                    Select all
                    {/* <span style={{ marginRight: "2px", marginLeft: "6px" }}>
                      ⌘
                    </span>
                    <span>A</span> */}
                  </>
                )
              }
              placement="bottom"
            >
              <Checkbox
                color="primary"
                indeterminate={!allStopsSelected}
                checked={allStopsSelected}
                onChange={handleSelectAllClick}
              />
            </OptimizedTooltip>
            <Typography className={classes.selectedText}>
              {numSelected} selected
            </Typography>
          </div>
          <Box display={"flex"} alignItems={"center"}>
            <>
              <MoveToVehicle
                routeGroups={routeGroups}
                routeId={showingRouteId}
                editId={selectedStopsList.list || []}
                setLoadingStop={setLoadingStop}
                isUnassigned
                isIcon
              />
              <Divider
                flexItem
                orientation="vertical"
                style={{ margin: "0 4px" }}
              />
            </>
            <ReactToPrint
              key={0}
              onBeforeGetContent={() => {
                setPrintingLabel(true);
                return new Promise((resolve, reject) => {
                  const interval = setInterval(() => {
                    if (readyToPrintLabel.current) {
                      clearInterval(interval);
                      resolve();
                    }
                  }, 500);
                });
              }}
              pageStyle={`
                    @media print {
                      body { 
                        -webkit-print-color-adjust: exact; 
                      }
                    }
                    @page { size: 100mm 150mm;  margin: 0; }
                  `}
              onAfterPrint={() => setPrintingLabel(false)}
              trigger={() => (
                <OptimizedTooltip
                  placement="bottom"
                  title={`Print shipping label${makeTextPlural(numSelected)}`}
                >
                  <span>
                    <IconButton className={classes.iconButton}>
                      <ReceiptIcon />
                    </IconButton>
                  </span>
                </OptimizedTooltip>
              )}
              content={() => {
                return labelsPrintRef.current;
              }}
            />
            <OptimizedTooltip
              placement="bottom"
              title={`Download stop${makeTextPlural(numSelected)}`}
            >
              <span>
                <IconButton
                  onClick={handleMultipleDownload}
                  className={classes.iconButton}
                >
                  <SaveAltIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>
            {!isPublished && numSelected === 0 && (
              <OptimizedTooltip placement="bottom" title={"Edit stop"}>
                <span>
                  <IconButton
                    className={classes.iconButton}
                    onClick={event => {
                      editStop(event, selectedStopsList?.list[0]);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
            {!isPublished && (
              <OptimizedTooltip
                placement="bottom"
                title={`Delete stop${makeTextPlural(numSelected)}`}
              >
                <span>
                  <IconButton
                    className={classes.iconButton}
                    onClick={() =>
                      deleteStop({
                        open: true,
                        index: null,
                        stop: null
                      })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
          </Box>
        </div>
      )}
      <Box display="none">
        <Box ref={labelsPrintRef}>
          {isPrintingLabel && (
            <PrintLabels
              data={stopsForPrint}
              billingDetails={billingDetails}
              organisation={organisation}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

const mapStateToProps = state => ({
  project: state.project,
  billingDetails: state.account.billingDetails,
  organisation:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation) ||
    {},
  isPublished: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  routeGroups: state.project.solution ? state.project.solution.routeGroups : []
});

export default connect(mapStateToProps)(UnscheduledToolbar);
